import {
    resolvedPromiseLike,
    type SyncOrAsync,
    type Result,
    setDifference,
    addAllToSet,
} from '@conduit-client/utils';
import { buildServiceDescriptor as buildCacheServiceDescriptor } from '@conduit-client/service-cache/v1';
import { CacheInclusionPolicyService } from './cache-inclusion-policy';
import type { Cache, Key } from '@conduit-client/service-cache/v1';

/**
 * Implementation of CacheInclusionPolicy that uses an inclusive
 * L2 durable cache as a second level behind an in memory, synchronous,
 * L1 cache.
 */
export abstract class DurableCacheInclusionPolicy extends CacheInclusionPolicyService {
    /**
     * Reads data out of a 2 level inclusive store.
     */
    read<D extends Result<void, unknown>>(options: {
        l1: Cache;
        readFromL1: (l1: Cache) => SyncOrAsync<D>;
        alreadyRevivedKeys?: Set<Key>;
    }): SyncOrAsync<D> {
        const { l1, readFromL1, alreadyRevivedKeys } = options;
        // eavesdrop on which keys read() accesses
        const recordableCache = l1.record();

        // let read() try to get the result
        return readFromL1(recordableCache).then((readResult) => {
            if (readResult.isOk()) {
                return readResult;
            }

            const missingKeys = recordableCache.missingKeysRead;

            // if we have already tried to revive all missing keys then
            // don't go through another revive cycle.
            // every key in alreadyRevivedKeys should be in L1, but filters on L1 might prevent
            // readFromL1 from seeing them, eg if the cache entry is expired; this check lets us
            // short-circuit the case where the L2 data for all the missing keys is known to be
            // unusable
            if (alreadyRevivedKeys && setDifference(missingKeys, alreadyRevivedKeys).size === 0) {
                return readResult;
            }

            return this.revive(missingKeys, l1).then((revivedKeys) => {
                // Only retry if this revive cycle brought new keys into L1.
                // Accumulate across iterations so we terminate when no
                // further progress can be made.
                const newlyRevived = alreadyRevivedKeys
                    ? setDifference(revivedKeys, alreadyRevivedKeys)
                    : revivedKeys;

                if (newlyRevived.size === 0) {
                    return readResult;
                }

                const allRevived = new Set(alreadyRevivedKeys ?? []);
                addAllToSet(allRevived, revivedKeys);
                return this.read({ l1, readFromL1, alreadyRevivedKeys: allRevived });
            });
        });
    }

    /**
     * Writes data to a 2 level inclusive store.
     */
    write<D extends Result<void, unknown>>(options: {
        l1: Cache;
        writeToL1: (l1: Cache) => SyncOrAsync<D>;
    }): SyncOrAsync<D> {
        const { l1, writeToL1 } = options;
        // eavesdrop on which keys write() accesses
        const tempL1 = buildCacheServiceDescriptor().service.record();

        // Write to a temp store to see what keys are ingested
        return writeToL1(tempL1).then(() => {
            // Try to read those keys from the existing store to see what keys are missing and need to be revived
            const keysToReviveRecordableCache = l1.record();

            tempL1.keysUpdated.forEach((key: Key) => keysToReviveRecordableCache.get(key));
            tempL1.missingKeysRead.forEach((key: Key) => keysToReviveRecordableCache.get(key));
            // Revive missing keys
            const missingKeys = keysToReviveRecordableCache.missingKeysRead;

            const revivePromiseLike =
                missingKeys.size > 0
                    ? this.revive(missingKeys, l1).then(() => undefined)
                    : resolvedPromiseLike(undefined);

            return revivePromiseLike.then(() => {
                // Do real write
                const recordableCache = l1.record();

                return writeToL1(recordableCache).then((result) => {
                    this.syncToL2Cache(recordableCache.keysUpdated, l1);
                    // don't let durable sync issues cause the write to fail
                    return result;
                });
            });
        });
    }

    /**
     * Revive entries from the durable store into the L1 cache.
     *
     * Called by the framework when data is missing from L1. The
     * implementation MUST look up each key in the durable store and,
     * for every key found, write the entry to L1 via {@link Cache.set}.
     *
     * @param missingKeys Keys that were absent from L1 during the most
     *   recent {@link read} or {@link write} attempt. Every key in this
     *   set is guaranteed to be missing from L1.
     * @param l1 The L1 cache instance. Revived entries MUST be written
     *   here so that subsequent readFromL1 calls can access them.
     * @returns The Set of keys that were found in the
     *   durable store and written to L1 on this invocation. The implementation MAY
     *   revive keys optimistically that are not explicitly requested.
     *
     *   The base implementation of {@link read} uses this set to detect whether progress was made.
     *   Returning no new keys across consecutive calls signals that no
     *   further revives are possible, and will terminate the retry loop.
     */
    abstract revive(missingKeys: Set<Key>, l1: Cache): SyncOrAsync<Set<Key>>;

    /**
     * Synchronize the data from an L1 cache into the persistent levels of your cache
     * by reading the keys present in keys from the l1Cache, and populating them in the
     * durable cache.
     *
     * @param changedKeys The set of keys to synchronize to the durable cache
     * @param l1 The L1 cache to synchronize the data from.
     */
    abstract syncToL2Cache(changedKeys: Set<Key>, l1: Cache): SyncOrAsync<void>;
}
